import React, { useState } from 'react'
import { TouchableOpacity } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon, GenericSelectionModal } from '@components'
import { useTranslatedRelationshipStatusStore } from '@store'
import { useUpdateProfileContexts, Contact } from '@hooks'
import { InfiniteData, useQueryClient } from '@tanstack/react-query'
import { SelectionItem } from '@utils'
import { useLanguage } from '@context'

type ContactsPage = { data?: Contact[] }
type ContactsInfiniteData = InfiniteData<ContactsPage>
type ContactsQuerySnapshot = [readonly unknown[], unknown][]

type Props = {
  contactId: string
  relationshipStatusId?: string | null
}

export const RelationshipStatusButton: React.FC<Props> = ({ contactId, relationshipStatusId }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const { statuses, getById } = useTranslatedRelationshipStatusStore()
  const { mutate: updateContexts } = useUpdateProfileContexts()
  const queryClient = useQueryClient()
  const [isVisible, setIsVisible] = useState(false)

  const current = getById(relationshipStatusId ?? '')
  const selected = current ? [current] : []

  const handleChange = (items: SelectionItem[]) => {
    const newId = items[0]?.id ?? null
    // Snapshot the cached lists before patching so a failed PATCH can roll
    // back the optimistic icon change (otherwise the icon stays wrong with
    // no feedback).
    const previousContacts = queryClient.getQueriesData({
      queryKey: ['contacts'],
    }) as ContactsQuerySnapshot
    // Patch this contact in place in the cached list instead of invalidating:
    // no refetch means the list keeps its scroll position and alphabetical
    // order, so the row doesn't jump when the status changes.
    queryClient.setQueriesData(
      { queryKey: ['contacts'] },
      (old: ContactsInfiniteData | undefined) => {
        if (!old?.pages) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: (page.data ?? []).map((c) =>
              c.id === contactId ? { ...c, relationshipStatusId: newId } : c,
            ),
          })),
        }
      },
    )
    updateContexts(
      { profileId: contactId, relationshipStatusId: newId },
      {
        onError: () => {
          // Restore the pre-patch lists so the icon reflects the real state.
          for (const [key, data] of previousContacts) {
            queryClient.setQueryData(key, data)
          }
        },
        onSettled: () => {
          void queryClient.invalidateQueries({ queryKey: ['contacts'] })
        },
      },
    )
    setIsVisible(false)
  }

  return (
    <>
      <TouchableOpacity
        onPress={() => setIsVisible(true)}
        hitSlop={8}
        accessibilityLabel={t('contextes.chooseRelationship')}
      >
        <Icon
          name={current?.icon || 'people'}
          size={theme.iconSize.small}
          color={current?.color || theme.colors.grey10}
        />
      </TouchableOpacity>

      <GenericSelectionModal
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        title={t('contextes.chooseRelationship')}
        placeholder={t('contextes.searchRelationship')}
        availableItems={statuses}
        selectedItems={selected}
        onSelectionChange={handleChange}
        multiSelect={false}
      />
    </>
  )
}
